import { create } from "zustand";
import {
  type Node,
  type Edge,
  type Connection,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
} from "@xyflow/react";

export interface GraphState {
  nodes: Node[];
  edges: Edge[];
  allNodes: Node[];
  allEdges: Edge[];
  visibleCategories: string[];
  selectedNodeId: string | null;
  isLoading: boolean;
  layoutMode: "custom" | "swimlane";
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  setSelectedNodeId: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
  setVisibleCategories: (categories: string[]) => void;
  applyCategoryFilter: (categories: string[]) => void;
  loadSavedView: (savedNodes: Node[], savedCategories: string[]) => void;
  setLayoutMode: (mode: "custom" | "swimlane") => void;
}

export const useGraphStore = create<GraphState>((set, get) => ({
  nodes: [],
  edges: [],
  allNodes: [],
  allEdges: [],
  visibleCategories: [
    "strategy",
    "people",
    "work",
    "sales",
    "finance",
    "operations",
    "risk",
    "documents",
    "innovation",
  ],
  selectedNodeId: null,
  isLoading: false,
  layoutMode: "custom",
  setNodes: (nodes) => {
    const currentAllNodes = get().allNodes;
    const newAllNodesMap = new Map();
    currentAllNodes.forEach((n) => newAllNodesMap.set(n.id, n));
    nodes.forEach((n) => newAllNodesMap.set(n.id, n));
    set({
      nodes,
      allNodes: Array.from(newAllNodesMap.values()),
    });
  },
  setEdges: (edges) => {
    const currentAllEdges = get().allEdges;
    const newAllEdgesMap = new Map();
    currentAllEdges.forEach((e) => newAllEdgesMap.set(e.id, e));
    edges.forEach((e) => newAllEdgesMap.set(e.id, e));
    set({
      edges,
      allEdges: Array.from(newAllEdgesMap.values()),
    });
  },
  onNodesChange: (changes) => {
    const currentNodes = get().nodes;
    const updatedNodes = applyNodeChanges(changes, currentNodes);

    // Sync to allNodes
    const allNodes = get().allNodes.map((node) => {
      const updated = updatedNodes.find((n) => n.id === node.id);
      return updated ? updated : node;
    });

    set({
      nodes: updatedNodes,
      allNodes,
    });
  },
  onEdgesChange: (changes) => {
    const currentEdges = get().edges;
    const updatedEdges = applyEdgeChanges(changes, currentEdges);

    // Sync to allEdges
    const allEdges = get().allEdges.map((edge) => {
      const updated = updatedEdges.find((e) => e.id === edge.id);
      return updated ? updated : edge;
    });

    set({
      edges: updatedEdges,
      allEdges,
    });
  },
  onConnect: (connection) => {
    const newEdges = addEdge(connection, get().edges);
    const newAllEdges = addEdge(connection, get().allEdges);
    set({
      edges: newEdges,
      allEdges: newAllEdges,
    });
  },
  setSelectedNodeId: (id) => set({ selectedNodeId: id }),
  setLoading: (isLoading) => set({ isLoading }),
  setVisibleCategories: (visibleCategories) => set({ visibleCategories }),
  applyCategoryFilter: (categories) => {
    const { allNodes, allEdges } = get();
    const filteredNodes = allNodes.filter((node) => {
      const nodeType = (node.data as any)?.type || "work";
      return categories.includes(nodeType);
    });
    const nodeIds = new Set(filteredNodes.map((n) => n.id));
    const filteredEdges = allEdges.filter(
      (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
    );
    set({
      nodes: filteredNodes,
      edges: filteredEdges,
      visibleCategories: categories,
    });
  },
  loadSavedView: (savedNodes, savedCategories) => {
    const allNodes = get().allNodes.map((node) => {
      const saved = savedNodes.find((n) => n.id === node.id);
      if (saved) {
        return {
          ...node,
          position: saved.position,
        };
      }
      return node;
    });

    const filteredNodes = allNodes.filter((node) => {
      const nodeType = (node.data as any)?.type || "work";
      return savedCategories.includes(nodeType);
    });

    const nodeIds = new Set(filteredNodes.map((n) => n.id));
    const filteredEdges = get().allEdges.filter(
      (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
    );

    set({
      allNodes,
      nodes: filteredNodes,
      edges: filteredEdges,
      visibleCategories: savedCategories,
    });
  },
  setLayoutMode: (layoutMode) => set({ layoutMode }),
}));
